---
title: Please optimize your photos
---

import { ImageComparison, ImageComparisonImage, ImageComparisonSlider } from '@/components/motion-primitives/image-comparison'
import { PhotoSizeChart } from '@/components/PhotoSizeChart'

Around 2.6 billion internet users are on connections slower than 10 Mbps. Mobile data is expensive and bandwidth is still limited in many regions of the world. Unoptimized images directly impact load times and data costs for these users. 

**As builders of the internet, it's our job to make sure things are accessible for these users.**

<ImageComparison className="aspect-[16/9] w-full rounded-lg border border-border">
  <ImageComparisonImage src="/thoughts/please-optimize-your-photos/original/reduce-motion.png" alt="Original PNG (2.6MB)" position="left" />
  <ImageComparisonImage src="/thoughts/please-optimize-your-photos/optimized/reduce-motion.webp" alt="Optimized WebP (42KB)" position="right" />
  <ImageComparisonSlider className="bg-highlight" />
</ImageComparison>

<p className="text-center text-sm text-muted-foreground mt-2">Drag to compare: Original PNG (2.6MB) vs optimized WebP (42KB)</p>

## ImageMagick

[ImageMagick](https://imagemagick.org/) is great for maually resizing and compress images in the terminal: 

```bash
convert input.jpg -resize 1920x1080\> -quality 80 output.jpg
```

(The `\>` flag only shrinks larger images without enlarging smaller ones.)

### Batch processing

You can also loop: 
```bash
for f in *.jpg; do
  convert "$f" -resize 1920x1080\> -quality 80 "optimized/$f"
done
```

### Strip metadata

This doesn't reduce a _tonne_ of size compared to the other methods on this page, but the privacy implications make it worth it to me anyway: 

```bash
convert input.jpg -strip -quality 80 output.jpg
```

### WebP conversion

WebP produces files 25-35% smaller than JPEG at equivalent quality:

```bash
convert input.jpg -quality 80 output.webp
```

[ImageMagick documentation →](https://imagemagick.org/script/command-line-processing.php)

## Optimizing images as part of the build

I use [sharp](https://sharp.pixelplumbing.com/) to optimize images as part of my build process:

```javascript
// optimize-images.js
import sharp from 'sharp'
import { glob } from 'glob'
import path from 'path'

const images = await glob('src/images/*.{jpg,png}')

for (const image of images) {
  const filename = path.basename(image, path.extname(image))
  
  await sharp(image)
    .resize(1920, 1080, { fit: 'inside', withoutEnlargement: true })
    .webp({ quality: 80 })
    .toFile(`dist/images/${filename}.webp`)
}
```

```json
// package.json
{
  "scripts": {
    "optimize-images": "node scripts/optimize-images.js",
    "build": "npm run optimize-images && vite build"
  }
}
```

## Responsive images

Not loading images that are way too big for the device they're being viewed on is my favourite way of reducing size: 

```javascript
// Generate multiple sizes during build
const sizes = [480, 768, 1024, 1920]

for (const image of images) {
  const filename = path.basename(image, path.extname(image))
  
  for (const width of sizes) {
    await sharp(image)
      .resize(width, null, { withoutEnlargement: true })
      .webp({ quality: 80 })
      .toFile(`dist/images/${filename}-${width}w.webp`)
  }
}
```

```html
<img
  src="/images/photo-1024w.webp"
  srcset="
    /images/photo-480w.webp 480w,
    /images/photo-768w.webp 768w,
    /images/photo-1024w.webp 1024w,
    /images/photo-1920w.webp 1920w
  "
  sizes="(max-width: 480px) 480px,
         (max-width: 768px) 768px,
         (max-width: 1024px) 1024px,
         1920px"
  alt="Photo"
/>
```

## Real-world example

Here's a real example from my photo gallery—a 4000×2667 landscape photo optimized with WebP at different responsive sizes:

<PhotoSizeChart />

<p className="text-center text-sm text-muted-foreground mt-2">File sizes for DSC08205-2.jpg at different responsive breakpoints</p>
